import { Typography } from '@karma/components';
import { COOKIE_SECTION_ID } from 'consts/querySelectors';
import { PATHS } from 'consts/paths';
import jumpToElement from 'functions/jumpToElement';
import InfoPageUnderlinedText from 'components/atoms/InfoPageUnderlinedText/InfoPageUnderlinedText';
import OpenCCPAFormButton from 'components/atoms/OpenCCPAFormButton/OpenCCPAFormButton';
import InfoPageBoldText from 'components/atoms/InfoPageBoldText/InfoPageBoldText';
import InfoPageParagraphText from 'components/atoms/InfoPageParagraphText/InfoPageParagraphText';
import ItemListLinkTermsPrivacy from 'components/atoms/ItemListLinkTermsPrivacy/ItemListLinkTermsPrivacy';
import InfoPageOrderedList from 'components/atoms/InfoPageOrderedList/InfoPageOrderedList';
import InfoPageListItem from 'components/atoms/InfoPageListItem/InfoPageListItem';
import InfoPageLinkSubtitle from 'components/atoms/InfoPageLinkSubtitle/InfoPageLinkSubtitle';
import InfoPageItalicText from 'components/atoms/InfoPageItalicText/InfoPageItalicText';
import InfoPageDarkText from 'components/atoms/InfoPageDarkText/InfoPageDarkText';
import LinkTermsPrivacy from 'components/atoms/LinkTermsPrivacy/LinkTermsPrivacy';
import InfoPagePrivacyParagraph from 'components/atoms/InfoPagePrivacyParagraph/InfoPagePrivacyParagraph';
import InfoPageTableCol from 'components/atoms/InfoPageTableCol/InfoPageTableCol';

const itemsPrivacyList = [
  { id: 0, nameLink: 'Personal Data We Collect', selector: '#PersonalDataWeCollect' },
  {
    id: 1,
    nameLink: 'Sharing the Personal Data We Collect',
    selector: '#SharingthePersonalDataWeCollect',
  },
  { id: 2, nameLink: 'International Transfer', selector: '#InternationalTransfer' },
  { id: 3, nameLink: 'Security', selector: '#Security' },
  {
    id: 4,
    nameLink: 'Your Rights - How to Access and Limit Our Use of Certain Personal Data',
    selector: '#YourRights',
  },
  { id: 5, nameLink: 'Data Retention', selector: '#DataRetention' },
  {
    id: 6,
    nameLink: 'Cookies and Similar Technologies',
    selector: '#CookiesandSimilarTechnologies',
  },
  { id: 7, nameLink: 'Third Party Sites and Services', selector: '#ThirdPartySitesandServices' },
  { id: 8, nameLink: 'Children', selector: '#Children' },
  { id: 9, nameLink: 'Communications', selector: '#Communications' },
  { id: 10, nameLink: 'Changes', selector: '#Changes' },
  { id: 11, nameLink: 'Comments and Questions', selector: '#CommentsandQuestions' },
  {
    id: 12,
    nameLink: 'Appendix A: Supplemental Privacy Notice for California and other U.S. State Residents',
    selector: '#AppendixA',
  },
  {
    id: 13,
    nameLink: 'Appendix B: Supplemental Privacy Notice for U.S. State Residents',
    selector: '#AppendixB',
  },
];

const PrivacyPageContent = () => {
  return (
    <>
      <InfoPageParagraphText>
        Karma Shopping Ltd., doing business as Karma (<InfoPageBoldText>&quot;Karma&quot;</InfoPageBoldText>,{' '}
        <InfoPageBoldText>&quot;us&quot;</InfoPageBoldText>, <InfoPageBoldText>&quot;we&quot;</InfoPageBoldText>, or{' '}
        <InfoPageBoldText>&quot;our&quot;</InfoPageBoldText>) respects your right to privacy. Your ability to make
        informed choices about the uses of your information is important to us. If not otherwise defined herein,
        capitalized terms have the meaning given to them in the Terms of Service, available at{' '}
        <LinkTermsPrivacy to={PATHS.TERMS}>https://www.karmanow.com/terms</LinkTermsPrivacy> (
        <InfoPageBoldText>&quot;Terms&quot;</InfoPageBoldText>). website at www.karmanow.com (
        <InfoPageBoldText>&quot;Site&quot;</InfoPageBoldText>) and/or use our mobile application including our browser
        extensions for safari and chrome (collectively the <InfoPageBoldText>&quot;App&quot;</InfoPageBoldText>) and/or
        our Services (<InfoPageBoldText>&quot;Services&quot;</InfoPageBoldText>).
      </InfoPageParagraphText>
      <InfoPageParagraphText>
        The Privacy Notice also explains how we collect, process, transfer, store and disclose the information
        collected, as well as your ability to control certain uses of the collected information.
      </InfoPageParagraphText>
      <InfoPageParagraphText>
        If you are an individual located in the European Union (
        <InfoPageBoldText>&quot;EU Individual&quot;</InfoPageBoldText>), some additional terms and rights may apply to
        you, as detailed herein. Karma Shopping Ltd. (Registered number: 514970250) is the data controller in respect of
        the processing activities outlined in this Privacy Notice. Our registered office is Menachem Begin St. 156, Tel
        - Aviv. Additional entities who may be serving as separate, independent controllers are listed below. You can
        contact our representative in the European Union at: PrighterGDPR-Rep by Maetzler Rechtsanwalts GmbH & Co KG,
        Attorneys at Law, c/o Karma, Schellinggasse 3/10, 1010 Vienna, Austria. Please include the subject
        &quot;GDPR-REP ID: 18180123&quot; on all correspondence. In addition, if you want to exercise your rights as a
        data subject, as described below, you can visit{' '}
        <LinkTermsPrivacy href="https://prighter.com/q/18180123">https://prighter.com/q/18180123</LinkTermsPrivacy>.
      </InfoPageParagraphText>
      <InfoPageParagraphText>
        Personal Data means any information that refers, is related to, or is associated with an identified or
        identifiable individual or as otherwise may be defined by applicable law. This Privacy Notice details which
        Personal Data is collected by us in connection with provision of the Site, App, and Services.
      </InfoPageParagraphText>
      <InfoPageParagraphText>
        The use of information received from Google APIs will adhere to the Chrome Web Store User Data Policy, including
        the Limited Use requirements.
      </InfoPageParagraphText>
      <Typography variant="h3">Privacy Notice Key Points</Typography>
      <InfoPageParagraphText>
        The key points listed below are presented in further detail throughout this Privacy Notice. You can click on the
        items in this section in order to find out more information about any topic.
      </InfoPageParagraphText>
      <InfoPageOrderedList>
        {itemsPrivacyList.map(item => {
          return (
            <ItemListLinkTermsPrivacy key={item.id} jumpToSection={() => jumpToElement(item.selector)}>
              {item.nameLink}
            </ItemListLinkTermsPrivacy>
          );
        })}
      </InfoPageOrderedList>
      <InfoPageOrderedList table>
        <InfoPageListItem level_1>
          <InfoPageLinkSubtitle id="PersonalDataWeCollect">Personal Data We Collect.</InfoPageLinkSubtitle>
          <InfoPageOrderedList>
            <InfoPageListItem level_2>
              Depending on your usage and geographic location, we collect different types of data and we and any of our
              third-party sub-contractors (<InfoPageBoldText>&quot;Sub Contractors&quot;</InfoPageBoldText>) and service
              providers we use or cooperate with (<InfoPageBoldText>&quot;Service Providers&quot;</InfoPageBoldText>)
              use the data we collect for different purposes, as specified below. It is your voluntary decision whether
              to provide us with certain Personal Data, but if you refuse to provide such Personal Data, we may not be
              able to register you and/or provide you with the Services or part thereof on our App and/or Site and/or
              any other platform we may use to provide our Services. See below a description of the Personal Data we
              collect for what purpose and the legal basis:
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Visitors to the Site.</InfoPageLinkSubtitle>
              <InfoPageOrderedList>
                <InfoPageListItem level_2>
                  <p>
                    <InfoPageLinkSubtitle>Contact Information.</InfoPageLinkSubtitle> If you contact us, whether by
                    submitting an online form that we make available or by sending an email to an email address that we
                    display on the Site with questions, comments, or for purposes of customer/technical support,
                    interest in advertising or regarding open positions, we collect the information contained in your
                    message and any other information, including Personal Data, you provide.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>How we use this data.</InfoPageLinkSubtitle> To respond to your request or
                    inquiry, to contact you regarding advertisement with us or regarding jobs at Karma.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>Legal Basis.</InfoPageLinkSubtitle> We process this Personal Data based on
                    legitimate interest when we respond to your request or inquiry.
                  </p>
                </InfoPageListItem>
                <InfoPageListItem level_2>
                  <p>
                    <InfoPageLinkSubtitle>IP Address and Site Activity.</InfoPageLinkSubtitle> When you visit the Site,
                    we will automatically collect, through cookies and other tracking technologies, information about
                    your computer, including non-Personal Data such as your operating system and browser type, and
                    Personal Data such as your IP address, browsing history, and information regarding your viewing
                    history on the Site. For more information about the cookies and similar technologies we use and how
                    to adjust your preferences, please see the section &quot;Cookies and Similar Technologies&quot;
                    below.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>How we use this data.</InfoPageLinkSubtitle> (1) to review usage and
                    operations, including in an aggregated non-specific analytical manner, develop new products or
                    services and improve current content, products, and Services; (2) to prevent fraud, protect the
                    security of our Site and Services, and address any problems with the Site and/or Services.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>Legal Basis.</InfoPageLinkSubtitle> Strictly necessary cookies which are
                    required for the proper and basic operation of the website will be processed in our legitimate
                    interest. Other cookies, including any analytic or marketing cookies, will be processed based on
                    your consent which we will obtain through our cookie notice and consent management.
                  </p>
                </InfoPageListItem>
              </InfoPageOrderedList>
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Users.</InfoPageLinkSubtitle> When you use the Services, we collect the following
              types of Personal Data from you:
              <InfoPageOrderedList>
                <InfoPageListItem level_2>
                  <p>
                    <InfoPageLinkSubtitle>Registration Data.</InfoPageLinkSubtitle> If you chose to register in order to
                    use our Services, you will be required to register and provide us with the following mandatory
                    Personal Data: your name and email address as well as a password that you will choose and use for
                    your account. If you use the Services by logging in through a third-party login/account registration
                    service (such as Facebook, Google, or Twitter), we also receive Personal Data about you as provided
                    by such third-party service, as determined by such third party&apos;s policies and your preferences.
                    You may choose to provide optional information such as your gender.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>How we Use this Data.</InfoPageLinkSubtitle> We use this Personal Data in
                    order to provide you with the Services and to respond to your inquiries and requests and to contact
                    and communicate with you and to provide you with informational newsletters and promotional materials
                    relating to our App and Services, including via email; and (2) to prevent fraud, protect the
                    security of and address any problems with the Site or App.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>Legal Basis.</InfoPageLinkSubtitle> (1) We process this Personal Data for the
                    purpose of providing the Services to you, which is considered performance of a contract with you,
                    including responding to your inquiries and requests and providing customer support.
                  </p>
                </InfoPageListItem>
                <InfoPageListItem level_2>
                  <p>
                    <InfoPageLinkSubtitle>Usage and Activity Data.</InfoPageLinkSubtitle> When you use the Services on
                    our App or Site, we will collect information about your mobile device, including non-Personal Data
                    such as your operating system, browser, device type, and Personal Data such as your IP address,
                    device ID, your browsing history and any information regarding your viewing and purchase history on
                    our Services. For more information about the cookies and similar technologies we use and how to
                    adjust your preferences, please see the section &quot;Cookies and Similar Technologies&quot; below.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>How we Use this Data.</InfoPageLinkSubtitle> (1) to review usage and
                    operations, including in an aggregated non-specific analytical manner, develop new products or
                    services and improve current content, products, and Services; (2) to prevent fraud, protect the
                    security of our Site, App, and Services, and address any problems with the Site, App, and/or
                    Services.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>Legal Basis.</InfoPageLinkSubtitle> Strictly necessary cookies which are
                    required for the proper and basic operation of the website will be processed in our legitimate
                    interest. Other cookies, including any analytic or marketing cookies, will be processed based on
                    your consent which we will obtain through our cookie notice and consent management.
                  </p>
                </InfoPageListItem>
                <InfoPageListItem level_2>
                  <p>
                    <InfoPageLinkSubtitle>Products Viewed.</InfoPageLinkSubtitle> We collect data regarding Products
                    that you have saved. This data includes the product name, price, color, size, description and the
                    link to the product on the applicable third-party online store. When you purchase products in
                    connection with our Services, we collect data regarding the products that you purchased. This data
                    includes purchase basket size, date of purchase and name of online store.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>How we Use this Data.</InfoPageLinkSubtitle> We use this Personal Data (1) in
                    order to provide you with the Services and to respond to your inquiries and requests and to contact
                    and communicate with you and (2) to review usage and operations, including in an aggregated
                    non-specific analytical manner, develop new products or services and improve current content,
                    products, and Services.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>Legal Basis.</InfoPageLinkSubtitle> (1) We process this Personal Data for the
                    purpose of providing the Services to you, which is considered performance of a contract with you.
                    (2) When we process this data in order to review usage, this is based on our legitimate interests to
                    develop our products and Services.
                  </p>
                </InfoPageListItem>
                <InfoPageListItem level_2>
                  <p>
                    <InfoPageLinkSubtitle>Browsing Data.</InfoPageLinkSubtitle> In case you a user of our browser
                    extensions we may collect data regarding web browsing data, which includes web pages visited,
                    clicked stream data and information about the content you viewed.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>How we Use this Data.</InfoPageLinkSubtitle> We use this Personal Data (1) in
                    order to provide you with the Services and feature of the extension and (2) we will share this data
                    in an aggregated, anonymized manner, for marketing research and commercial use with our business
                    partners.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>Legal Basis.</InfoPageLinkSubtitle> (1) We process this Personal Data for the
                    purpose of providing the Services to you, which is considered performance of a contract with you.
                    (2) When we process and share the aggregated and anonymized data we will ask for your consent.
                  </p>
                </InfoPageListItem>
              </InfoPageOrderedList>
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Rewards Program.</InfoPageLinkSubtitle> When you participate in the Rewards Program,
              we collect the following types of Personal Data from you:
              <InfoPageOrderedList>
                <InfoPageListItem level_2>
                  <p>
                    <InfoPageLinkSubtitle>Transaction Information.</InfoPageLinkSubtitle> We collect data relating to
                    Qualifying Purchases and other transactions you have made in connection with earning Coins under the
                    Rewards Program. You can see a record of your transactions within your account dashboard. This data
                    is collected automatically through cookies. For more information about the cookies and similar
                    technologies we use, please see the section &quot;Cookies and Similar Technologies&quot; below.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>How we Use this Data.</InfoPageLinkSubtitle> We use this Personal Data in
                    order to track Qualifying Purchases, confirm or authenticate transactions and provide you with the
                    Rewards Program.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>Legal Basis.</InfoPageLinkSubtitle> We process this Personal Data for the
                    purpose of providing the Coins to you, which is considered performance of a contract with you.
                  </p>
                </InfoPageListItem>
                <InfoPageListItem level_2>
                  <p>
                    <InfoPageLinkSubtitle>Data Received from Partner.</InfoPageLinkSubtitle> When you choose to
                    participate in Rewarded Activities, we may receive the following personal data from the Partner:
                    Confirmation of activity completion (for example, app installation and usage duration, game
                    progress, or survey completion) and reward eligibility status; Attribution data (e.g., click time,
                    install time); User identifier (as linked with Partner ID).
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>How we Use this Data.</InfoPageLinkSubtitle> To validate user actions,
                    determine eligibility, calculate Coins, and trigger Coins crediting to your Karma account and
                    related redemption flows.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>Legal Basis.</InfoPageLinkSubtitle> We process this Personal Data for the
                    purpose of providing the applicable Coins, which is considered performance of a contract with you.
                  </p>
                </InfoPageListItem>
                <InfoPageListItem level_2>
                  <p>
                    <InfoPageLinkSubtitle>Data Shared with Partner.</InfoPageLinkSubtitle> When you choose to
                    participate in Rewarded Activities, we may provide the following personal data to the Partner: User
                    ID.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>How we Use this Data.</InfoPageLinkSubtitle> To match reward flows, calculate
                    and credit Coins and optimize targeting based on campaign configuration.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>Legal Basis.</InfoPageLinkSubtitle> When we process this for reward flows
                    (including calculating and crediting Coins), we do so based on performance of a contract with you.
                    When we process this data targeting optimization we do so based on our legitimate interest to
                    improve and enhance our platforms and offerings.
                  </p>
                </InfoPageListItem>
                <InfoPageListItem level_2>
                  <p>
                    <InfoPageLinkSubtitle>Paypal Account Information.</InfoPageLinkSubtitle> We collect details relating
                    to the Paypal account that you have designated to receive redemption of Coins.{' '}
                    <InfoPageItalicText>
                      Note that if a User has provided us with your PayPal account details and has designated you to
                      receive the withdrawal, we process these details in fulfillment of our contract with the
                      applicable User.
                    </InfoPageItalicText>
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>How we Use this Data.</InfoPageLinkSubtitle> We use this Personal Data in
                    order to enable Coins redemption.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>Legal Basis.</InfoPageLinkSubtitle> We process this Personal Data for the
                    purpose of enabling redemption of Coins, which is considered performance of a contract with you.
                  </p>
                </InfoPageListItem>
                <InfoPageListItem level_2>
                  <p>
                    <InfoPageLinkSubtitle>Gift Card Redemption Information.</InfoPageLinkSubtitle> If you redeem Coins
                    for a gift card, we may collect and/or generate information needed to issue and fulfill the gift
                    card (such as the selected gift card type, gift card value, and gift card code or identifier). In
                    order to process redemption requests, we collect your email address and may require you to verify
                    and confirm the redemption via email before processing. Gift card redemption options may vary based
                    on your country or geographic region, and we may collect or infer location data to determine
                    available redemption options. For gift cards issued in a currency other than U.S. Dollars,
                    information regarding currency conversion costs and/or fees may also be collected and processed. The
                    relevant redemption details will be shared with the applicable gift card issuer, who acts as a
                    separate data controller.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>How we Use this Data.</InfoPageLinkSubtitle> We process this Personal Data for
                    the purposes of (i) enabling and fulfilling gift card redemptions (including enabling your selection
                    of gift card type, verifying redemption requests via email, and determining available redemption
                    options based on your geographic location), and (ii) generating analytics and statistical insights
                    and improving and optimizing the Rewards Program and our Services (including understanding
                    redemption preferences and trends).
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>Legal Basis.</InfoPageLinkSubtitle> When we process this Personal Data for the
                    purposes of enabling and fulfilling gift card redemptions, we do so on the basis of performance of a
                    contract with you, and when we process this Personal Data for the purposes of generating analytics
                    and insights we do so based on our legitimate interests to improve our services and provide better
                    services to you.
                  </p>
                </InfoPageListItem>
                <InfoPageListItem level_2>
                  <p>
                    <InfoPageLinkSubtitle>Surprise Box Promotions Information.</InfoPageLinkSubtitle> If you choose to
                    participate in a Surprise Box Promotion (as described in the Terms), we may collect and generate
                    information relating to your participation, such as your Ticket purchases, the Coins used to
                    purchase Tickets (including information about the source category of such Coins for eligibility
                    purposes), promotion start/end times applicable to you, claim activity, and records relating to
                    winner selection and prize fulfillment (including gift card amount, delivery status, and the email
                    address used for delivery).
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>How we Use this Data.</InfoPageLinkSubtitle> We use this Personal Data to
                    administer and operate Surprise Box Promotions, including to process Ticket purchases, determine
                    eligibility, prevent fraud and abuse, select and verify winners, deliver prizes and provide
                    notifications relating to the promotion, and to respond to user inquiries.
                  </p>
                  <p>
                    <InfoPageLinkSubtitle>Legal Basis.</InfoPageLinkSubtitle> We process this Personal Data for the
                    purpose of administering the promotion and providing the relevant Services to you, which is
                    considered performance of a contract with you, and for fraud prevention, security, and compliance
                    purposes based on our legitimate interests and/or legal obligations, as applicable.
                  </p>
                </InfoPageListItem>
              </InfoPageOrderedList>
            </InfoPageListItem>
            <InfoPageLinkSubtitle>Additional Uses</InfoPageLinkSubtitle>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Statistical Information.</InfoPageLinkSubtitle> By analyzing all of the information
              we receive, including all information concerning users we may compile statistical information across a
              variety of platforms and users (&quot;Statistical Information&quot;). Statistical Information helps us
              understand trends and customer needs so that new products and services can be considered and so that
              existing products and services can be tailored to customer desires. Statistical Information may be linked
              to Personal Data. We may share such Statistical Information with our partners, without restriction, on
              commercial terms that we can determine in our sole discretion.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Analytics.</InfoPageLinkSubtitle> We and/or our Service Providers or Sub
              Contractors, use analytics tools (<InfoPageDarkText>&quot;Tools&quot;</InfoPageDarkText>), including
              Google analytics to collect information about the use of the Site, App, or Services. Such Tools collect
              information such as how often users visit the Site or App, what pages they visit when they do so, and what
              other sites and mobile applications they used prior to visiting the Site or App. We use the information we
              get from the Tools to improve our Site, App, and Services. Google&apos;s ability to use and share
              information collected by Google Analytics about your visits to this site is restricted by the Google
              Analytics Terms of Service located at&nbsp;
              <LinkTermsPrivacy href="http://www.google.com/analytics/terms/us.html">
                http://www.google.com/analytics/terms/us.html
              </LinkTermsPrivacy>
              &nbsp;and the Google Privacy Policy located at&nbsp;
              <LinkTermsPrivacy href="http://www.google.com/policies/privacy/">
                http://www.google.com/policies/privacy/
              </LinkTermsPrivacy>
              .
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Direct Marketing.</InfoPageLinkSubtitle> As described above, we may use Personal
              Data to let you know about our products and Services that we believe will be of interest to you and, if
              you have consented, about products and services of third parties. We may contact you by email or through
              other communication channels. In all cases, we will respect your preferences for how you would like us to
              manage marketing activity with respect to you. To protect privacy rights and to ensure you have control
              over how we manage marketing with you:
              <InfoPageOrderedList>
                <InfoPageListItem level_2>
                  We will take steps to limit direct marketing to a reasonable and proportionate level and only send you
                  communications which we believe may be of interest or relevance to you.
                </InfoPageListItem>
                <InfoPageListItem level_2>
                  At any time you can update or correct your personal profile within your account, or change your
                  preferences for the way in which you would like us to communicate with you, including how you receive
                  details of latest offers or newsletters from us.
                </InfoPageListItem>
                <InfoPageListItem level_2>
                  You can ask us to stop sending email marketing by following the &quot;unsubscribe&quot; link you will
                  find on all the email marketing messages we send you. Alternatively, you can contact us at{' '}
                  <LinkTermsPrivacy href="mailto:support@karmanow.com" target="_top">
                    support@karmanow.com
                  </LinkTermsPrivacy>
                  .
                </InfoPageListItem>
                <InfoPageListItem level_2>
                  You can change the way your browser manages cookies, which may be used to deliver online advertising,
                  by following the settings on your browser as explained below.
                </InfoPageListItem>
                <InfoPageListItem level_2>
                  If our marketing activities are based upon your consent, you may withdraw this consent at any time by
                  changing your preferences on your personal profile.
                </InfoPageListItem>
              </InfoPageOrderedList>
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Legal Uses.</InfoPageLinkSubtitle> We may use your Personal Data as required or
              permitted by any applicable law, for example, to comply with audit and other legal requirements.
            </InfoPageListItem>
          </InfoPageOrderedList>
        </InfoPageListItem>
        <InfoPageListItem level_1>
          <InfoPageLinkSubtitle id="SharingthePersonalDataWeCollect">
            Sharing the Personal Data We Collect.
          </InfoPageLinkSubtitle>{' '}
          We share your information, including Personal Data, as follows:
          <InfoPageOrderedList>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Business Partners.</InfoPageLinkSubtitle> Karma works with certain third-party
              business partners through affiliate marketing programs. This means that with respect to (some of your)
              interactions on the website, such as saving items, clicking on links to third-party websites, clicking on
              advertising links, or purchasing an item, Karma may earn commissions from its business partners. The data
              that will be shared with those business partners however we will not share any details of your activities
              or items you have tagged with them. In addition Karma may share aggregated, anonymized data related to the
              browsing activities of our extension users with some of our business partners.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <p>
                <InfoPageLinkSubtitle>Service Providers, and Sub Contractors.</InfoPageLinkSubtitle> We also disclose
                information, including Personal Data we collect from and/or about you, to our trusted Service Providers
                and Sub Contractors, who have agreed to confidentiality restrictions and who use such information solely
                on our behalf in order to: (1) help us provide you with the Site, App, and/or Services; (2) aid in their
                understanding of how users are using our Site, App, and/or Services; (3) for the purpose of direct
                marketing (see above for more details).
              </p>
              <p>
                Such Service Providers and Sub Contractors provide us with IT and system administration services, data
                backup, security, and storage services, data analysis, and help us serve advertisements and provide
                other marketing services.
              </p>
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Data Controllers.</InfoPageLinkSubtitle> When you use our Services, we also disclose
              your Personal Data to additional third parties, such as business partners, which act as independent,
              separate controllers with respect to the collection of your Personal Data. The details and contact
              information of such controllers are as set forth below.
            </InfoPageListItem>
            <table>
              <colgroup>
                <InfoPageTableCol width="30%" />
                <InfoPageTableCol width="70%" />
              </colgroup>
              <tbody>
                <tr>
                  <th>
                    <LinkTermsPrivacy href="https://rakutenmarketing.com/legal-notices/services-privacy-policy">
                      Rakuten Marketing
                    </LinkTermsPrivacy>
                  </th>
                  <th>215 Park Avenue, 2nd floor, New York, NY, USA</th>
                </tr>

                <tr>
                  <th>
                    <LinkTermsPrivacy href="https://rakutenmarketing.com/legal-notices/services-privacy-policy">
                      Adjoe
                    </LinkTermsPrivacy>
                  </th>
                  <th>An d. Alster 42, 20099 Hamburg, Germany</th>
                </tr>

                <tr>
                  <th>AdTraction</th>
                  <th>199 Bishopsgate, London, EC2M 3TY, United Kingdom</th>
                </tr>
                <tr>
                  <th>
                    <LinkTermsPrivacy href="https://www.awin.com/gb/legal/privacy-policy">AWIN</LinkTermsPrivacy>
                  </th>
                  <th>Eichhornstraße 3, 10785 Berlin, Germany</th>
                </tr>
                <tr>
                  <th>Commission Junction</th>
                  <th>530 E Montecito StreetSanta Barbara, CA 93103, USA</th>
                </tr>
                <tr>
                  <th>Commission Factory</th>
                  <th>Suite 401, 32 Martin Place, Sydney, NSW 2000, Australia</th>
                </tr>
                <tr>
                  <th>Flex Offers</th>
                  <th>990 Biscayne Blvd Office #1402, USA</th>
                </tr>
                <tr>
                  <th>Impact Radius</th>
                  <th>223 E. De La Guerra St., Santa Barbara, CA 93101, USA</th>
                </tr>
                <tr>
                  <th>
                    <LinkTermsPrivacy href="https://www.pepperjam.com/legal#privacy">PepperJam</LinkTermsPrivacy>
                  </th>
                  <th>555 E North Ln, Conshohocken, PA 19428, USA</th>
                </tr>
                <tr>
                  <th>Performance Horizons</th>
                  <th>8th Floor, West One, Forth Banks, Newcastle Upon Tyne, United Kingdom, NE1 3PA</th>
                </tr>
                <tr>
                  <th>Share-A-Sale</th>
                  <th>15 W. Hubbard St. STE 500, Chicago IL 60654, USA</th>
                </tr>
                <tr>
                  <th>SkimLinks</th>
                  <th>52 Bevenden Street, London N1 6BL, United Kingdom</th>
                </tr>
                <tr>
                  <th>Target</th>
                  <th>233 East De La Guerra, Santa Barbara, California, USA 93101</th>
                </tr>
                <tr>
                  <th>Viglink/Sovrn</th>
                  <th>5541 Central Avenue, Suite 100, Boulder, CO 80301, USA</th>
                </tr>
                <tr>
                  <th>Cuelinks</th>
                  <th>
                    D/6, Goragandhi Shopping Center, Opp Railway Stn. Chandavarkar Road, Borivali - West, Mumbai - 400
                    091
                  </th>
                </tr>
                <tr>
                  <th>Yieldkit</th>
                  <th>Jarrestraße 44 b, 22303 Hamburg, Germany</th>
                </tr>
                <tr>
                  <th>TradeDoubler</th>
                  <th>Tradedoubler Ltd.4th Floor, 90 High Holborn, WC1V 6LJ London</th>
                </tr>
                <tr>
                  <th>Soicos</th>
                  <th>Moldes 3333, CP 1429, Capital Federal, Buenos Aires, Argentina</th>
                </tr>
                <tr>
                  <th>NOCTEMQUE</th>
                  <th>Camp-King-Allee 17, 61440 Oberursel (Taunus), Germany</th>
                </tr>
                <tr>
                  <th>Apple</th>
                  <th>8th Floor, West One, Forth Banks, Newcastle Upon Tyne, United Kingdom, NE1 3PA</th>
                </tr>
                <tr>
                  <th>Linkbux</th>
                  <th>5th Floor, #3 Minrun Building, 1388 Yishan Road, Xuhui District, Shanghai,China</th>
                </tr>
                <tr>
                  <th>Digidip</th>
                  <th>Skalitzer Str. 68, 10997 Berlin, Germany</th>
                </tr>
                <tr>
                  <th>Brandreward</th>
                  <th>7600 Chevy Chase Drive Chase Park, Suite 300 Austin, TX 78752, USA</th>
                </tr>
                <tr>
                  <th>Avantlink</th>
                  <th>1922 Pro1922 Prospector Park City, Utah 84060 United Statesspector Park City, Utah 84060 USA</th>
                </tr>
                <tr>
                  <th>Webgains</th>
                  <th>21 Farringdon Road, London, EC1M 3HA, UK</th>
                </tr>
                <tr>
                  <th>Revoffers</th>
                  <th>315 S. Coast Highway 101, #528 - Encinitas, CA 92024, USA</th>
                </tr>
                <tr>
                  <th>Linkconnector</th>
                  <th>1001 Winstead Drive Suite 300 Cary, NC 27513, USA</th>
                </tr>
                <tr>
                  <th>Goaffpro</th>
                  <th>1st Floor, 159 D, new mandi Sirsa, Haryana, 125055, India</th>
                </tr>
                <tr>
                  <th>Refersion</th>
                  <th>242 W 38th St, New York, NY 10018, USA</th>
                </tr>
                <tr>
                  <th>Tradetracker</th>
                  <th>70 Wapping Wall London, E1W 3SS, UK</th>
                </tr>
                <tr>
                  <th>Intango/RevenueHits</th>
                  <th>HaBarzel St 38, Tel Aviv-Yafo, Israel, 6971054</th>
                </tr>
                <tr>
                  <th>Sourceknowledge</th>
                  <th>1001 Boulevard De Maisonneuve Wes Suite 1510 Montreal, Quebec H3A 3C8, Canada</th>
                </tr>
                <tr>
                  <th>Connexity</th>
                  <th>2120 COLORADO AVE. STE. 400 SANTA MONICA, CA 90404, USA</th>
                </tr>
              </tbody>
            </table>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Business Transfers.</InfoPageLinkSubtitle> Your Personal Data may be disclosed as
              part of, or during negotiations of, any merger, sale of company assets or acquisition (including in cases
              of liquidation) in such case, your Personal Data shall continue being subject to the provisions of this
              Privacy Notice.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Law Enforcement Related Disclosure.</InfoPageLinkSubtitle> We may share your
              Personal Data with third parties: (i) if we believe in good faith that disclosure is appropriate to
              protect our or a third party&apos;s rights, property or safety (including the enforcement of the Terms and
              this Privacy Notice); (ii) when required by law, regulation subpoena, court order or other law enforcement
              related issues, agencies and/or authorities; or (iii) as is necessary to comply with any legal and/or
              regulatory obligation.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Other Uses or Transfer of Your Personal Data.</InfoPageLinkSubtitle> If you use our
              Services together with or through a third-party service, site and/or mobile application, we may receive
              information (including Personal Data) about you from those third parties. Please note that when you use
              third-parties outside of our Site, App, or Services, their own terms and privacy policies will govern your
              use of those services.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Disclosure of Mobile Data.</InfoPageLinkSubtitle> While we do share certain Personal
              Data with others as described above, we want to make it clear that we will not sell or share your phone
              number or any other information related to your mobile phone or device with third parties to enable those
              third parties to market products or services to you that are not ours. That being said, if you consent to
              such transfer and your consent permits us to transfer your Personal Data under applicable law, we will do so.
            </InfoPageListItem>
          </InfoPageOrderedList>
        </InfoPageListItem>
        <InfoPageListItem level_1>
          <InfoPageLinkSubtitle id="InternationalTransfer">International Transfer.</InfoPageLinkSubtitle>
          <InfoPageOrderedList>
            <InfoPageListItem level_2>
              We use Sub Contractors and Service Providers and have affiliates who are located in countries other than
              your own and send them information we receive (including Personal Data). We conduct such international
              transfers for the purposes described above. We will ensure that these third parties will be subject to
              written agreements ensuring the same level of privacy and data protection as set forth in this Privacy
              Notice, including appropriate remedies in the event of the violation of your data protection rights in
              such third country.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              Whenever we transfer your Personal Data to third parties based outside of the European Economic Area (
              <InfoPageDarkText>&quot;EEA&quot;</InfoPageDarkText>), we ensure a similar degree of protection is
              afforded to it by ensuring at least one of the following safeguards is implemented:
              <InfoPageOrderedList>
                <InfoPageListItem level_2>
                  We will only transfer your Personal Data to countries that have been deemed to provide an adequate
                  level of protection for Personal Data by the European Commission.
                </InfoPageListItem>
                <InfoPageListItem level_2>
                  Where we use certain service providers, we may use specific contracts approved by the European
                  Commission which give Personal Data the same protection it has in the EEA.
                </InfoPageListItem>
              </InfoPageOrderedList>
            </InfoPageListItem>
            <InfoPageListItem level_2>
              Please contact us at{' '}
              <LinkTermsPrivacy href="mailto:support@karmanow.com" target="_top">
                support@karmanow.com
              </LinkTermsPrivacy>{' '}
              if you would like further information on the specific mechanism used by us when transferring your Personal
              Data out of the EEA.
            </InfoPageListItem>
          </InfoPageOrderedList>
        </InfoPageListItem>
        <InfoPageListItem level_1>
          <InfoPageLinkSubtitle id="Security">Security.</InfoPageLinkSubtitle> We have implemented and maintain
          appropriate technical and organization security measures, policies and procedures designed to reduce the risk
          of accidental destruction or loss, or the unauthorized disclosure or access to Personal Data appropriate to
          the nature of such data. The measures we take include:
          <InfoPageOrderedList>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Safeguards</InfoPageLinkSubtitle> – The physical, electronic, and procedural
              safeguards we employ to protect your Personal Data include secure servers and SSL encryption of data.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Access Control</InfoPageLinkSubtitle> – We dedicate efforts for a proper management
              of system entries and limit access only to authorized personnel on a need to know basis of least privilege
              rules, review permissions once per month, and revoke access immediately after employee termination.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Internal Policies</InfoPageLinkSubtitle> – We maintain and regularly review and
              update our privacy related and information security policies.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Personnel</InfoPageLinkSubtitle> – We require new employees to sign non-disclosure
              agreements according to applicable law and industry customary practice.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Encryption</InfoPageLinkSubtitle> – We encrypt the data in transit using secure
              HTTPS protocols.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Database Backup</InfoPageLinkSubtitle> – Our databases are backed up on a daily
              basis for certain data and are verified regularly. Backups are encrypted and stored within the production
              environment to preserve their confidentiality and integrity, are tested regularly to ensure availability,
              and are accessible only by authorized personnel.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              However, no method of transmission over the Internet or method of electronic storage is 100% secure.
              Therefore, while we strive to use commercially acceptable means to protect your Personal Data, we cannot
              guarantee its absolute security.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              As the security of information depends in part on the security of the computer you use to communicate with
              us and the security you use to protect user IDs and passwords, please take appropriate measures to protect
              this information.
            </InfoPageListItem>
          </InfoPageOrderedList>
        </InfoPageListItem>
        <InfoPageListItem level_1>
          <InfoPageLinkSubtitle id="YourRights">
            Your Rights - How to Access and Limit Our Use of Certain Personal Data.
          </InfoPageLinkSubtitle>{' '}
          Subject to certain exemptions, and in some cases dependent upon the processing activity we are undertaking,
          you have certain rights in relation to the Personal Data that we or other controllers hold about you, as
          detailed below. For any requests to exercise such rights with respect to information held by other
          controllers, please contact the applicable controller directly. If you wish for us to notify all independent
          controllers, please specify that request when you contact us in order to exercise any of your rights. We will
          investigate and attempt to resolve complaints and disputes and make every reasonable effort to honor your wish
          to exercise your rights as quickly as possible and in any event, within the timescales provided by applicable
          data protection laws. We reserve the right to ask for reasonable evidence to verify your identity before we
          provide you with any information and/or comply with any of your requests, as detailed below:
          <InfoPageOrderedList>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Right of Access.</InfoPageLinkSubtitle> You have a right to know what Personal Data
              we collect about you and, in some cases, to have such Personal Data communicated to you. Subject to
              applicable law, we may charge you with a fee. Please note that we may not be able to provide you with all
              the information you request, and, in such case, we will endeavor to explain to you why.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Right to Data Portability.</InfoPageLinkSubtitle> If the processing is based on your
              consent or performance of a contract with you and processing is being carried out by automated means, you
              may be entitled to (request that we) provide you or another party with a copy of the Personal Data you
              provided to us in a structured, commonly-used, and machine-readable format.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Right to Correct Personal Data.</InfoPageLinkSubtitle> Subject to the limitations in
              applicable law, you may request that we update, complete, correct or delete inaccurate, incomplete, or
              outdated Personal Data.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Deletion of Personal Data</InfoPageLinkSubtitle> (&quot;Right to Be
              Forgotten&quot;). You have a right to request that we delete your Personal Data if either: (i) it is no
              longer needed for the purpose for which it was collected, (ii) our processing was based on your consent
              and you have withdrawn your consent, (iii) you have successfully exercised your Right to Object (see
              below), (iv) processing was unlawful, or (iv) we are required to erase it for compliance with a legal
              obligation. We cannot restore information once it has been deleted. Please note that to ensure that we do
              not collect any further Personal Data, you should also delete our App from your mobile devices, terminate
              your account with us, and clear our cookies from any device where you have used our Site or App. We may
              retain certain Personal Data (including following your request to delete) for audit and record-keeping
              purposes, or as otherwise permitted and/or required under applicable law.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Right to Restrict Processing.</InfoPageLinkSubtitle> You can ask us to limit the
              processing of your Personal Data if either: (i) you have contested its accuracy and wish us to limit
              processing until this is verified; (ii) the processing is unlawful, but you do not wish us to erase the
              Personal Data; (iii) it is no longer needed for the purposes for which it was collected, but we still need
              it to establish, exercise, or defend of a legal claim; (iv) you have exercised your Right to Object
              (below) and we are in the process of verifying our legitimate grounds for processing. We may continue to
              use your Personal Data after a restriction request under certain circumstances.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Direct Marketing Opt Out.</InfoPageLinkSubtitle> You can change your mind at any
              time about your election to receive marketing communications from us and/or having your Personal Data
              processed for direct marketing purposes. If you do, please notify us by contacting us at{' '}
              <LinkTermsPrivacy href="mailto:support@karmanow.com" target="_top">
                support@karmanow.com
              </LinkTermsPrivacy>
              . We will process your request as soon as reasonably possible, however it may take a few days for us to
              update our records before any opt out is effective.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Right to Object.</InfoPageLinkSubtitle> You can object to any processing of your
              Personal Data which has our legitimate interests as its legal basis, if you believe your fundamental
              rights and freedoms outweigh our legitimate interests. If you raise an objection, we have an opportunity
              to demonstrate that we have compelling legitimate interests which override your rights and freedoms.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Withdrawal of Consent.</InfoPageLinkSubtitle> You may withdraw your consent in
              connection with any processing of your Personal Data based on a previously granted consent. This will not
              affect the lawfulness of any processing prior to such withdrawal.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>
                Right to Lodge a Complaint with Your Local Supervisory Authority.
              </InfoPageLinkSubtitle>{' '}
              You may have the right to submit a complaint to the relevant supervisory data protection authority if you
              have any concerns about how we are processing your Personal Data, though we ask that as a courtesy you
              please attempt to resolve any issues with us first. List of EAA Supervisory Authorities can be found at{' '}
              <LinkTermsPrivacy href="https://edpb.europa.eu/about-edpb/about-edpb/members_en">
                https://edpb.europa.eu/about-edpb/about-edpb/members_en
              </LinkTermsPrivacy>
              .
            </InfoPageListItem>
          </InfoPageOrderedList>
        </InfoPageListItem>
        <InfoPageListItem level_1>
          <InfoPageLinkSubtitle id="DataRetention">Data Retention</InfoPageLinkSubtitle>
          <InfoPageOrderedList>
            <InfoPageListItem level_2>
              Subject to applicable law, we retain Personal Data as necessary for the purposes set forth above. We may
              delete information from our systems without notice to you once we deem it is no longer necessary for these
              purposes. Retention by any of our processors may vary in accordance with the processor&apos;s retention
              policy. Your data will be retained for as long as you have a registered account.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              In some circumstances, we may store your Personal Data for longer periods of time, for instance where we
              are required to do so in accordance with legal, regulatory, tax, audit, accounting requirements and so
              that we have an accurate record of your dealings with us in the event of any complaints or challenges, or
              if we reasonably believe there is a prospect of litigation relating to your Personal Data or dealings. To
              determine the appropriate retention period, we consider the amount, nature, and sensitivity of the
              Personal Data, the potential risk of harm from unauthorized use or disclosure of your Personal Data, the
              purposes for which we process your Personal Data, and whether those purposes can be achieved through other
              means, as well as applicable legal requirements.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              Please contact us at{' '}
              <LinkTermsPrivacy href="mailto:support@karmanow.com" target="_top">
                support@karmanow.com
              </LinkTermsPrivacy>{' '}
              if you would like details regarding the retention periods for different types of your Personal Data.
            </InfoPageListItem>
          </InfoPageOrderedList>
        </InfoPageListItem>
        <InfoPageListItem level_1 id={COOKIE_SECTION_ID}>
          <InfoPageLinkSubtitle id="CookiesandSimilarTechnologies">
            Cookies and Similar Technologies
          </InfoPageLinkSubtitle>
          <InfoPageOrderedList>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>What are Cookies?</InfoPageLinkSubtitle> A cookie is a small piece of text that is
              sent to a user&apos;s browser or device. The browser provides this piece of text to the device of the
              originating user when this visitor returns.
              <InfoPagePrivacyParagraph>
                A &quot;session cookie&quot; is temporary and will remain on your device until you leave the Site.
              </InfoPagePrivacyParagraph>
              <InfoPagePrivacyParagraph>
                A &quot;persistent&quot; cookie may be used to help save your settings and customizations across visits.
                It will remain on your device for much longer or until you delete it.
              </InfoPagePrivacyParagraph>
              <InfoPagePrivacyParagraph>
                First-party cookies are placed by us, while third-party cookies may be placed by a third party. We use
                both first- and third-party cookies.
              </InfoPagePrivacyParagraph>
              <InfoPagePrivacyParagraph>
                Information may also be collected through web beacons, which are small graphic images (&quot;pixel
                tags&quot;), which usually work together with cookies in order to identify users and user behavior.
                These may be shared with third parties.
              </InfoPagePrivacyParagraph>
              We may use the terms &quot;cookies&quot; to refer to all technologies that we may use to store data in
              your browser or device or that collect information or help us identify you in the manner described above.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <p>
                <InfoPageLinkSubtitle>How We Use Cookies.</InfoPageLinkSubtitle> We use cookies and similar technologies
                for a number of reasons, for example, in order to help personalize your experience. Third parties
                through which we provide the Services and/or our business partners may be placing and reading cookies on
                your browser or using web beacons to collect information. When visiting the Site, you shall be notified
                of the use of and placement of cookies and other similar technologies on your device as specified
                herein.
              </p>
              <p>
                The specific names and types of the cookies, web beacons, and other similar technologies we use may
                change from time to time. However, the cookies we use generally fall into one of the following
                categories:
              </p>
            </InfoPageListItem>
            <table>
              <colgroup>
                <InfoPageTableCol width="30%" />
                <InfoPageTableCol width="70%" />
              </colgroup>
              <thead>
                <tr>
                  <th>
                    <InfoPageDarkText>Type of Cookie</InfoPageDarkText>
                  </th>
                  <th>
                    <InfoPageDarkText>Why We Use These Cookies</InfoPageDarkText>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>Necessary</th>
                  <th>
                    These cookies are necessary in order to allow the App to work correctly. They enable you to access
                    the App, move around, and access different services, features, and tools. Examples include
                    remembering previous actions (e.g. entered text) when navigating back to a page in the same session.
                    These cookies cannot be disabled.
                  </th>
                </tr>
                <tr>
                  <th>Functionality</th>
                  <th>
                    These cookies remember your settings and preferences and the choices you make (such as language or
                    regional preferences) in order to help us personalize your experience and offer you enhanced
                    functionality and content.
                  </th>
                </tr>
                <tr>
                  <th>Security</th>
                  <th>
                    These cookies can help us identify and prevent security risks. They may be used to store your
                    session information to prevent others from changing your password without your login information.
                  </th>
                </tr>
                <tr>
                  <th>Performance</th>
                  <th>
                    These cookies can help us collect information to help us understand how you use our App, such as
                    whether you have viewed messages or specific pages and how long you spent on each page. This helps
                    us improve the performance of our App.
                  </th>
                </tr>
                <tr>
                  <th>Analytics</th>
                  <th>
                    These cookies help us learn more about which features are popular with our users and how our App can
                    be improved.
                  </th>
                </tr>
                <tr>
                  <th>Advertising</th>
                  <th>
                    These cookies are placed in order to deliver content, including ads relevant and meaningful to you
                    and your interests. They may also be used to deliver targeted advertising or to limit the number of
                    times you see an advertisement. This can help us track how efficient advertising campaigns are, both
                    for our own Services and for other websites. Such cookies may track your browsing habits and
                    activity when visiting our Platform and those of third-parties.{' '}
                  </th>
                </tr>
              </tbody>
            </table>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Rewards Program.</InfoPageLinkSubtitle> We place cookies in order to track and
              authenticate Qualifying Purchases made in connection with the Rewards Program. These cookies are placed
              when you click on an &quot;activate&quot; button before being linked to the retailer&apos;s website and
              will stay active for a maximum period of 24 hours, after which they will expire.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>How to Adjust Your Preferences.</InfoPageLinkSubtitle> Most Web browsers are
              initially configured to accept cookies, but you can change this setting so your browser either refuses all
              cookies or informs you when a cookie is being sent. In addition, you are free to delete any existing
              cookies at any time. Please note that some features of the Services may not function properly when cookies
              are disabled or removed. For example, if you delete cookies that store your account information or
              preferences, you will be required to input these each time you visit. Additionally, if you delete or
              disable cookies that are used to track purchases made in connection with the Karma Gives Program, you will
              not be eligible to receive Rewards in respect of those purchases.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageLinkSubtitle>Third Party Cookies.</InfoPageLinkSubtitle> We use the following third-party
              cookies:
            </InfoPageListItem>
            <table>
              <colgroup>
                <InfoPageTableCol width="35%" />
                <InfoPageTableCol width="30%" />
                <InfoPageTableCol width="35%" />
              </colgroup>
              <tbody>
                <tr>
                  <th>Google Analytics</th>
                  <th>Session ID</th>
                  <th>Analytics Cookie</th>
                </tr>
                <tr>
                  <th>Skimlinks</th>
                  <th>Session ID</th>
                  <th>Advertising Cookie</th>
                </tr>
                <tr>
                  <th>Linkshare</th>
                  <th>Session ID</th>
                  <th>Advertising Cookie</th>
                </tr>
                <tr>
                  <th>Viglink</th>
                  <th>Session ID</th>
                  <th>Advertising Cookie</th>
                </tr>
                <tr>
                  <th>Impact Radius</th>
                  <th>Session ID</th>
                  <th>Advertising Cookie</th>
                </tr>
                <tr>
                  <th>Affiliate Window</th>
                  <th>Session ID</th>
                  <th>Advertising Cookie</th>
                </tr>
                <tr>
                  <th>Share A Sale</th>
                  <th>Session ID</th>
                  <th>Advertising Cookie</th>
                </tr>
                <tr>
                  <th>Commission Factory</th>
                  <th>Session ID</th>
                  <th>Advertising Cookie</th>
                </tr>
                <tr>
                  <th>Braze</th>
                  <th>Session ID</th>
                  <th>Advertising Cookie</th>
                </tr>
                <tr>
                  <th>Commission Junction</th>
                  <th>Session ID</th>
                  <th>Advertising Cookie</th>
                </tr>
                <tr>
                  <th>Partnerize</th>
                  <th>Session ID</th>
                  <th>Advertising Cookie</th>
                </tr>
                <tr>
                  <th>Pepperjam</th>
                  <th>Session ID</th>
                  <th>Advertising Cookie</th>
                </tr>
                <tr>
                  <th>Facebook</th>
                  <th>Session ID</th>
                  <th>Advertising Cookie</th>
                </tr>
                <tr>
                  <th>Google Ads</th>
                  <th>Session ID</th>
                  <th>Advertising Cookie</th>
                </tr>
                <tr>
                  <th>CJ</th>
                  <th>Session ID</th>
                  <th>Advertising Cookie</th>
                </tr>
                <tr>
                  <th>Kustomer</th>
                  <th>Session ID</th>
                  <th>Performance Cookie</th>
                </tr>
              </tbody>
            </table>
            Additionally, cookies may be placed in messages we send in order to track your interaction with such emails.
          </InfoPageOrderedList>
        </InfoPageListItem>
        <InfoPageListItem level_1>
          <InfoPageLinkSubtitle id="ThirdPartySitesandServices">Third Party Sites and Services.</InfoPageLinkSubtitle>{' '}
          Portions of the Services may involve linking to or using websites or services belonging to third parties,
          including those of third-party vendors or retailers. All use of third-party applications or services is at
          your own risk and subject to such third party&apos;s privacy policies.
        </InfoPageListItem>
        <InfoPageListItem level_1>
          <InfoPageLinkSubtitle id="Children">Children.</InfoPageLinkSubtitle> Karma does not knowingly collect Personal
          Data from children under the age of 16. In the event you become aware that an individual under the age of 16
          has enrolled without parental permission, please advise us immediately at{' '}
          <LinkTermsPrivacy href="mailto:support@karmanow.com" target="_top">
            support@karmanow.com
          </LinkTermsPrivacy>
          .
        </InfoPageListItem>
        <InfoPageListItem level_1>
          <InfoPageLinkSubtitle id="Communications">Communications.</InfoPageLinkSubtitle> As part of the Services, we
          may send you e-mail or other messages and/or a newsletter about us or our Services. You may also elect to
          receive notifications from us regarding sales, coupons and products that are &quot;back in stock&quot; or
          &quot;low in stock&quot;, as well as the addition of new stores to our Services. You may remove your Personal
          Data from our mailing list and stop receiving future communication from us by following the UNSUBSCRIBE link
          located at the bottom of each communication or by emailing us at{' '}
          <LinkTermsPrivacy href="mailto:support@karmanow.com" target="_top">
            support@karmanow.com
          </LinkTermsPrivacy>
          . You will also be given the opportunity to unsubscribe from commercial messages in any such email or other
          message we send as well as through your account settings. Please note that we reserve the right to send you
          service-related communications, including service announcements and administrative messages, relating either
          to your account or to your transactions on these Services, without offering you the opportunity to opt out of
          receiving them. Should you not wish to receive such communications, you may cancel your account.
        </InfoPageListItem>
        <InfoPageListItem level_1>
          <InfoPageLinkSubtitle id="Changes">Changes.</InfoPageLinkSubtitle> When visiting our Platform, you shall be
          asked to accept the terms of this Privacy Notice. If you do not agree with the terms hereof, please do not use
          the Platform or Services. We may update this Privacy Notice from time to time - in which case, we shall notify
          you of such changes. We will post the updated Privacy Notice on this page. Please come back to this page every
          now and then to make sure you are familiar with the latest version. Any new Privacy Notice will be effective
          from the date it is accepted by you.
        </InfoPageListItem>
        <InfoPageListItem level_1>
          <InfoPageLinkSubtitle id="CommentsandQuestions">Comments and Questions.</InfoPageLinkSubtitle> If you have any
          comments or questions about this Privacy Notice, or if you wish to exercise any of your legal rights as set
          out herein, please contact us at{' '}
          <LinkTermsPrivacy href="mailto:support@karmanow.com" target="_top">
            support@karmanow.com
          </LinkTermsPrivacy>
          .
        </InfoPageListItem>
        <InfoPageListItem level_1>
          <InfoPageLinkSubtitle id="AppendixA">
            Appendix A: Supplemental Privacy Notice for California and other U.S. State Residents
          </InfoPageLinkSubtitle>
          <p>
            This Supplemental Privacy Notice for California Residents (
            <InfoPageDarkText>&quot;CA Supplement&quot;</InfoPageDarkText>) supplements and forms an integral part of
            our general privacy notice, available at{' '}
            <LinkTermsPrivacy href="https://www.karmanow.com/" target="_top">
              https://www.karmanow.com/
            </LinkTermsPrivacy>{' '}
            (<InfoPageDarkText>&quot;Privacy Notice&quot;</InfoPageDarkText>) . This CA Supplement describes how we
            collect, share, and store personal information about California residents who visit our Site and/or use our
            App and/or Services, as well as the rights they have with respect to that information. If not otherwise
            defined herein, capitalized terms have the meaning given to them in our Privacy Notice. Please read the
            Privacy Notice for additional terms that may apply to you.{' '}
          </p>
          <p>
            <InfoPageDarkText>&quot;Personal Information&quot;</InfoPageDarkText> means any information that identifies,
            relates to, describes, is reasonably capable of being associated with, or could reasonably be linked,
            directly or indirectly, with a particular consumer or household, or as otherwise may be defined by
            applicable law.
          </p>
          <p>
            Depending on your usage, we collect different types of information and we and any of our third-party
            sub-contractors and service providers use the information we collect for different purposes, as specified
            below. It is your voluntary decision whether to provide us with certain Personal Information, but if you
            refuse to provide such Personal Information, we may not be able to register you and/or provide you with the
            Services or part thereof.
          </p>
          <div>
            This CA Supplement will cover the following topics:
            <ol>
              <li>
                <InfoPageUnderlinedText
                  onClick={() => jumpToElement('#categoriesOfPersonalInformationWeCollect')}
                  style={{ cursor: 'pointer' }}
                >
                  <InfoPageDarkText>Categories of Personal Information We Collect</InfoPageDarkText>
                </InfoPageUnderlinedText>
              </li>
              <li>
                <InfoPageUnderlinedText
                  onClick={() => jumpToElement('#purposesOfCollectingYourPersonalInformation')}
                  style={{ cursor: 'pointer' }}
                >
                  <InfoPageDarkText>Purposes of Collecting your Personal Information</InfoPageDarkText>
                </InfoPageUnderlinedText>
              </li>
              <li>
                <InfoPageUnderlinedText
                  onClick={() => jumpToElement('#howWeShareYourPersonalInformation')}
                  style={{ cursor: 'pointer' }}
                >
                  <InfoPageDarkText>How We Share Your Personal Information</InfoPageDarkText>
                </InfoPageUnderlinedText>
              </li>
              <li>
                <InfoPageUnderlinedText
                  onClick={() => jumpToElement('#yourRightsHowToAccess')}
                  style={{ cursor: 'pointer' }}
                >
                  <InfoPageDarkText>
                    Your Rights - How to Access and Limit Our Use of Certain Personal Information
                  </InfoPageDarkText>
                </InfoPageUnderlinedText>
              </li>
              <li>
                <InfoPageUnderlinedText onClick={() => jumpToElement('#howToContactUs')} style={{ cursor: 'pointer' }}>
                  <InfoPageDarkText>How to Contact Us</InfoPageDarkText>
                </InfoPageUnderlinedText>
              </li>
            </ol>
          </div>
          <InfoPageOrderedList>
            <InfoPageListItem level_2 id="categoriesOfPersonalInformationWeCollect">
              <InfoPageDarkText>Categories of Personal Information We Collect.</InfoPageDarkText> In the past twelve
              months, we have collected the following Personal Information from visitors to our Site and users of our
              App and/or Services, including members of our Rewards Program and/or Coins associated with Rewarded
              Activities.
              <table>
                <colgroup>
                  <InfoPageTableCol width="30%" />
                  <InfoPageTableCol width="45%" />
                  <InfoPageTableCol width="30%" />
                </colgroup>
                <tbody>
                  <tr>
                    <th>
                      <InfoPageDarkText>Category</InfoPageDarkText>
                    </th>
                    <th>
                      <InfoPageDarkText>Example</InfoPageDarkText>
                    </th>
                    <th>
                      <InfoPageDarkText>Collected</InfoPageDarkText>
                    </th>
                  </tr>
                  <tr>
                    <th>A. Identifiers.</th>
                    <th>
                      A real name, alias, postal address, unique personal identifier, online identifier, Internet
                      Protocol address, email address, account name, Social Security number, driver&apos;s license
                      number, passport number, or other similar identifiers.
                    </th>
                    <th>
                      <InfoPageDarkText>Yes</InfoPageDarkText>: Online identifier, unique online identifier, IP address,
                      email address, name.
                    </th>
                  </tr>
                  <tr>
                    <th>
                      B. Personal information categories listed in the California Customer Records statute (Cal. Civ.
                      Code § 1798.80(e)).
                    </th>
                    <th>
                      A name, signature, Social Security number, physical characteristics or description, address,
                      telephone number, passport number, driver&apos;s license or state identification card number,
                      insurance policy number, education, employment, employment history, bank account number, credit
                      card number, debit card number, or any other financial information, medical information, or health
                      insurance information. <br />
                      Some personal information included in this category may overlap with other categories.
                    </th>
                    <th>
                      <InfoPageDarkText>Yes</InfoPageDarkText>: Paypal account details.
                    </th>
                  </tr>
                  <tr>
                    <th>C. Protected classification characteristics under California or federal law.</th>
                    <th>
                      Age (40 years or older), race, color, ancestry, national origin, citizenship, religion or creed,
                      marital status, medical condition, physical or mental disability, sex (including gender, gender
                      identity, gender expression, pregnancy or childbirth and related medical conditions), sexual
                      orientation, veteran or military status, genetic information (including familial genetic
                      information).
                    </th>
                    <th>
                      <InfoPageDarkText>No</InfoPageDarkText>
                    </th>
                  </tr>
                  <tr>
                    <th>D. Commercial information.</th>
                    <th>
                      Records of personal property, products or services purchased, obtained, or considered, or other
                      purchasing or consuming histories or tendencies.
                    </th>
                    <th>
                      <InfoPageDarkText>Yes</InfoPageDarkText>: Products purchased including the products name, price,
                      color, size, description, the link to the product on the applicable third-party online store, the
                      purchase basket size, date of purchase, name of online store, and including as related to Surprise
                      Box Promotion and Tickets. Information regarding Qualifying Purchases and participation in
                      Rewarded Activities, including completion status and reward eligibility. Gift card redemption
                      selections and PayPal account details for reward withdrawals.
                    </th>
                  </tr>
                  <tr>
                    <th>E. Biometric information.</th>
                    <th>
                      Genetic, physiological, behavioral, and biological characteristics, or activity patterns used to
                      extract a template or other identifier or identifying information, such as, fingerprints, face
                      prints, and voiceprints, iris or retina scans, keystroke, gait, or other physical patterns, and
                      sleep, health, or exercise data.
                    </th>
                    <th>
                      <InfoPageDarkText>No</InfoPageDarkText>
                    </th>
                  </tr>
                  <tr>
                    <th>F. Internet or other similar network activity.</th>
                    <th>
                      Browsing history, search history, information on a consumer&apos;s interaction with a website,
                      application, or advertisement.
                    </th>
                    <th>
                      <InfoPageDarkText>Yes</InfoPageDarkText>: Browsing history, search history, access time and data,
                      search queries products viewed, online stores visited, interaction with advertisements.
                    </th>
                  </tr>
                  <tr>
                    <th>G. Geolocation data.</th>
                    <th>Physical location, approximate location derived from IP address or movements.</th>
                    <th>
                      <InfoPageDarkText>Yes</InfoPageDarkText>: Approximate location.
                    </th>
                  </tr>
                  <tr>
                    <th>H. Sensory data.</th>
                    <th>Audio, electronic, visual, thermal, olfactory, or similar information.</th>
                    <th>
                      <InfoPageDarkText>No</InfoPageDarkText>
                    </th>
                  </tr>
                  <tr>
                    <th>I. Professional or employment-related information.</th>
                    <th>Current or past job history or performance evaluations.</th>
                    <th>
                      <InfoPageDarkText>No</InfoPageDarkText>
                    </th>
                  </tr>
                  <tr>
                    <th>
                      J. Non-public education information (per the Family Educational Rights and Privacy Act (20 U.S.C.
                      Section 1232g, 34 C.F.R. Part 99)).
                    </th>
                    <th>
                      Education records directly related to a student maintained by an educational institution or party
                      acting on its behalf, such as grades, transcripts, class lists, student schedules, student
                      identification codes, student financial information, or student disciplinary records.
                    </th>
                    <th>
                      <InfoPageDarkText>No</InfoPageDarkText>
                    </th>
                  </tr>
                  <tr>
                    <th>K. Inferences drawn from other personal information.</th>
                    <th>
                      Profile reflecting a person&apos;s preferences, characteristics, psychological trends,
                      predispositions, behavior, attitudes, intelligence, abilities, and aptitudes.
                    </th>
                    <th>
                      <InfoPageDarkText>No</InfoPageDarkText>
                    </th>
                  </tr>
                  <tr>
                    <th>L. Sensitive personal information.</th>
                    <th>
                      Government-issued identifying numbers, financial account details, genetic data, precise
                      geolocation, race or ethnicity, religious or philosophical beliefs, union membership, mail, email,
                      text messages, biometric data, health data, and sexual orientation or sex life.
                    </th>
                    <th>
                      <InfoPageDarkText>No</InfoPageDarkText>
                    </th>
                  </tr>
                </tbody>
              </table>
            </InfoPageListItem>
            <InfoPageListItem level_2 id="purposesOfCollectingYourPersonalInformation">
              <InfoPageDarkText>
                <InfoPageDarkText>Purposes of Collecting your Personal Information.</InfoPageDarkText>
              </InfoPageDarkText>{' '}
              We collect the Personal Information listed above for following purposes:
              <InfoPageOrderedList>
                <InfoPageListItem level_3>
                  <InfoPageDarkText>Contact Information:</InfoPageDarkText> We collect this Personal Information in
                  order to respond to your inquiry or request.
                </InfoPageListItem>
                <InfoPageListItem level_3>
                  <InfoPageDarkText>Registration Information:</InfoPageDarkText> We collect this Personal Information in
                  order to provide you with the Services, to contact and communicate with you, to provide you with
                  informational newsletters and promotional materials relating to our Site, App, and Services, including
                  via email, and to prevent fraud, protect the security of and address any problems with the Site or
                  App.
                </InfoPageListItem>
                <InfoPageListItem level_3>
                  <InfoPageDarkText>Device/Network Information:</InfoPageDarkText> We collect this Personal Information
                  in order to review usage and operations, including in an aggregated non-specific analytical manner,
                  develop new products or services and improve current content, products, and Services. We also use this
                  Personal Information to prevent fraud, protect the security of our Site, App, and Services, and
                  address any problems with the Site, App, and/or Services.
                </InfoPageListItem>
                <InfoPageListItem level_3>
                  <InfoPageDarkText>Product/Usage Information:</InfoPageDarkText> To provide you with the Services, to
                  respond to your inquiries and requests, and to contact and communicate with you. Furthermore, we use
                  this information to review usage and operations, including in an aggregated non-specific analytical
                  manner, to develop new products or services, and to improve current content, products, and Services.
                </InfoPageListItem>
                <InfoPageListItem level_3>
                  <InfoPageDarkText>PayPal Account Information:</InfoPageDarkText> To enable redemption of Coins earned
                  through the Rewards Program.
                </InfoPageListItem>
              </InfoPageOrderedList>
            </InfoPageListItem>
            <InfoPageListItem level_2 id="howWeShareYourPersonalInformation">
              <InfoPageDarkText>How We Share Your Personal Information.</InfoPageDarkText> In the past 12 months, we
              have disclosed the following categories of Personal Information for the following business or commercial
              purpose in the preceding 12 months:
              <InfoPageOrderedList>
                <InfoPageListItem level_3>
                  <InfoPageDarkText>Information Shared for Business Purposes.</InfoPageDarkText> We have shared your{' '}
                  <InfoPageDarkText>personal identifiers</InfoPageDarkText> and{' '}
                  <InfoPageDarkText>internet or other electronic network activity</InfoPageDarkText> with data analytics
                  providers, operating systems, and other third parties that assist us in providing you with the
                  Services, including providing us with IT and system administration services, data backup, security,
                  and storage services, and providing data analytics services, and to help us serve advertisements and
                  provide other marketing services.
                </InfoPageListItem>
                <InfoPageListItem level_3>
                  <InfoPageDarkText>Information Shared for Commercial Purposes.</InfoPageDarkText> We work with certain
                  third-party business partners through affiliate marketing programs and share certain{' '}
                  <InfoPageDarkText>personal identifiers</InfoPageDarkText> and{' '}
                  <InfoPageDarkText>internet or other electronic network activity</InfoPageDarkText> with them in a
                  manner that would be considered a &quot;sale&quot;. With respect to (some of your) interactions with
                  the Site, such as saving items, clicking on links to third-party websites, clicking on advertising
                  links, or purchasing an item, we may earn commissions from our business partners, however we will not
                  share any details of your activities or items you have tagged with them. You may direct us at any time
                  to cease selling your Personal Information by submitting a request to opt-out in the manner detailed
                  below. We do not sell the Personal Information of minors which we have actual knowledge are under the
                  age of 16 without affirmative authorization. While we do share certain Personal Data with others as
                  described above, we want to make it clear that we will not sell or share your phone number or any
                  other information related to your mobile phone or device with third parties to enable those third
                  parties to market products or services to you that are not ours. That being said, if you consent to
                  such transfer and your consent permits us to transfer your Personal Data under applicable law, we will
                  do so.
                </InfoPageListItem>

                <InfoPageListItem level_3>
                  <InfoPageDarkText>Sharing of Aggregated and non-identifying data.</InfoPageDarkText> We sell
                  aggregated non-identifying Web Browsing Data. The aggregated non-identifying web browsing data is
                  collected from our extension, which collect, upon your consent, your Web Browsing Data this web
                  browsing data is filtered and aggregated and then sold to our business partners and used for
                  commercial purposes. These customers include companies active in the advertising and marketing sector
                  such as data analytics, market measurement and marketing companies, as well as brands, eCommerce
                  vendors. As such aggregated and non-identifying data do not include any personal information and are
                  based on completely aggregated data which does not able to identify an individual, thus, they do not
                  fall to the CCPA scope as detailed in Section A above and is not defined as a &quot;sale&quot; or
                  &quot;share&quot;. You can opt-out from sharing the web browsing data at any time through the
                  extension settings.
                </InfoPageListItem>

                <InfoPageListItem level_3>
                  <InfoPageDarkText>Additional Sharing Activities</InfoPageDarkText>
                  <InfoPageOrderedList>
                    <InfoPageListItem level_4>
                      <InfoPageDarkText>Business Transfers:</InfoPageDarkText> Your Personal Information may be
                      disclosed as part of, or during negotiations of, any merger, sale of company assets or acquisition
                      (including in cases of liquidation). In such a case, your Personal Information shall continue
                      being subject to the provisions of this CA Supplement.
                    </InfoPageListItem>
                    <InfoPageListItem level_4>
                      <InfoPageDarkText>Law Enforcement Related Disclosure:</InfoPageDarkText> We may share your
                      Personal Information with third parties: (i) if we believe in good faith that such disclosure is
                      appropriate to protect our or a third party&apos;s rights, property or safety (including the
                      enforcement of the Terms, the Privacy Notice, and this CA Supplement); (ii) when required by law,
                      regulation subpoena, court order or other law enforcement related issues, agencies and/or
                      authorities; or (iii) as is necessary to comply with any legal and/or regulatory obligation.
                    </InfoPageListItem>
                    <InfoPageListItem level_4>
                      <InfoPageDarkText>Other Uses or Transfer of Your Personal Information:</InfoPageDarkText> If you
                      use our Services together with or through a third-party service, site and/or mobile application,
                      we may receive information (including Personal Information) about you from those third parties.
                      Please note that when you use third parties outside of our Site, App, or Services, their own terms
                      and privacy policies will govern your use of those services.
                    </InfoPageListItem>
                  </InfoPageOrderedList>
                </InfoPageListItem>
              </InfoPageOrderedList>
            </InfoPageListItem>
            <InfoPageListItem level_2 id="yourRightsHowToAccess">
              <InfoPageDarkText>
                Your Rights - How to Access and Limit Our Use of Certain Personal Information.
              </InfoPageDarkText>{' '}
              Subject to certain exemptions, you have certain rights in relation to the Personal Information that we
              have collected about you, as detailed below. In accordance with applicable law, we may ask for reasonable
              evidence to verify your identity before we comply with certain of your requests, as detailed below.
              <InfoPageOrderedList>
                <InfoPageListItem level_3>
                  <InfoPageDarkText>Your Rights</InfoPageDarkText>
                  <InfoPageOrderedList>
                    <InfoPageListItem level_4>
                      <InfoPageDarkText>Right of Access.</InfoPageDarkText> You have the right to request that we
                      disclose to you, subject to applicable law, any or all of the following in respect of the 12-month
                      period preceding your request, or such other period required by applicable law:
                      <ul>
                        <li>The specific Personal Information we have collected about you.</li>
                        <li>The categories of Personal Information we have collected about you.</li>
                        <li>The categories of sources from which we collected the Personal Information about you.</li>
                        <li>The categories of third parties with whom we share Personal Information about you.</li>
                        <li>
                          The categories of Personal Information about you we have sold and the categories of third
                          parties to whom we have sold such Personal Information.
                        </li>
                        <li>
                          The categories of Personal Information about you we have disclosed for a business purpose and
                          the categories of third parties to whom we have disclosed such Personal Information.
                        </li>
                        <li>
                          Our business or commercial purpose(s) for collecting or selling your Personal Information.
                        </li>
                      </ul>
                    </InfoPageListItem>
                    <InfoPageListItem level_4>
                      <InfoPageDarkText>Right to Delete.</InfoPageDarkText> Subject to certain exceptions, you have the
                      right to request that we and any of our service providers delete your Personal Information.
                    </InfoPageListItem>
                    <InfoPageListItem level_4>
                      <InfoPageDarkText>Right to Correct.</InfoPageDarkText> If you find that any of the Personal
                      Information we&apos;ve collected about you is inaccurate, you have the right to request that we
                      correct such information, subject to applicable law.
                    </InfoPageListItem>
                    <InfoPageListItem level_4>
                      <InfoPageDarkText>
                        Right to Non-Discrimination for Exercising your Consumer Privacy Rights.
                      </InfoPageDarkText>{' '}
                      You have the right not to be discriminated against for exercising any of your consumer privacy
                      rights, such as, not being denied any goods or services or charged different prices or rates.
                    </InfoPageListItem>
                  </InfoPageOrderedList>
                </InfoPageListItem>
                <InfoPageListItem level_2>
                  <InfoPageDarkText>
                    How to Exercise your California and other U.S. State Privacy Rights.
                  </InfoPageDarkText>
                  <InfoPageOrderedList>
                    <InfoPageListItem level_4>
                      <InfoPageDarkText>Contact Information.</InfoPageDarkText> To exercise any of the rights detailed
                      above, please submit a verifiable request to us by contacting us at{' '}
                      <LinkTermsPrivacy href="mailto:support@karmanow.com" target="_top">
                        support@karmanow.com
                      </LinkTermsPrivacy>
                      . Where available, you may also exercise certain opt-out choices through any cookie consent
                      manager, privacy settings, or other privacy choice tools we make available through the Site or
                      App. You may only request to exercise your right of access twice within a 12-month period, or as
                      otherwise permitted by applicable law.
                    </InfoPageListItem>
                    <InfoPageListItem level_4>
                      <InfoPageDarkText>Submitting a Verifiable Request.</InfoPageDarkText> In order to exercise your
                      right to know or right to delete you must submit a request containing sufficient information that
                      allows us to reasonably verify you are the person about whom we collected the applicable Personal
                      Information or an authorized agent of such person, which may include details relating to your
                      account. Any requests made through your password protected account will be verified through our
                      existing authentication procedures for such account.
                    </InfoPageListItem>
                    <InfoPageListItem level_4>
                      <InfoPageDarkText>Submitting Requests through an Authorized Agent.</InfoPageDarkText> An
                      authorized agent may exercise requests on your behalf, to the extent permitted by applicable law.
                      In order to exercise your right to know or right to delete through an agent, we may ask for
                      reasonable evidence to verify your identity and the agent’s identity, and written authorization
                      permitting the agent to act on your behalf before complying with your request. In order to submit
                      a request to opt-out of the sale of your Personal Information through an agent, we may ask for
                      written authorization permitting the authorized agent to act on your behalf before complying with
                      your request. We reserve the right to deny the request of any agent that does not provide proof
                      that they have been authorized to act on behalf of the applicable consumer in accordance with
                      applicable law.
                    </InfoPageListItem>
                    <InfoPageListItem level_4>
                      <InfoPageDarkText>Appeals.</InfoPageDarkText> If we decline to take action on your request, you
                      may have the right to appeal our decision, depending on your state of residence. To submit an
                      appeal, please contact us at{' '}
                      <LinkTermsPrivacy href="mailto:support@karmanow.com" target="_top">
                        support@karmanow.com
                      </LinkTermsPrivacy>{' '}
                      and include &quot;Privacy Rights Appeal&quot; in the subject line, together with sufficient
                      information for us to identify your original request and the basis for your appeal. We will review
                      and respond to your appeal within the timeframe required by applicable law. If your appeal is
                      denied, you may have the right to contact your state attorney general or other applicable
                      regulator.
                    </InfoPageListItem>
                  </InfoPageOrderedList>
                </InfoPageListItem>
                <InfoPageListItem level_3>
                  <InfoPageDarkText>Children.</InfoPageDarkText> We do not knowingly sell or share the Personal
                  Information of consumers under 16 years of age, or process such Personal Information for targeted
                  advertising or any other purpose for which opt-in consent is required under applicable law, unless we
                  have obtained such consent as required by applicable law.
                </InfoPageListItem>
              </InfoPageOrderedList>
            </InfoPageListItem>
            <InfoPageListItem level_2 id="howToContactUs">
              <InfoPageDarkText>How to Contact Us.</InfoPageDarkText> If you have any comments or questions about this
              U.S. State Supplement or our privacy practices, or if you wish to exercise any of your legal rights as set
              out herein, please contact us at{' '}
              <LinkTermsPrivacy href="mailto:support@karmanow.com" target="_top">
                support@karmanow.com
              </LinkTermsPrivacy>
              .
              <div style={{ marginTop: '10px' }}>
                <OpenCCPAFormButton />
              </div>
            </InfoPageListItem>
          </InfoPageOrderedList>
        </InfoPageListItem>

        <InfoPageListItem level_1>
          <InfoPageLinkSubtitle id="AppendixB">
            Appendix B: Supplemental Privacy Notice for U.S. State Residents
          </InfoPageLinkSubtitle>
          <InfoPageOrderedList>
            <InfoPageListItem level_2>
              This Supplemental Privacy Notice for Residents of Other U.S. States (
              <InfoPageDarkText>&quot;U.S. State Supplement&quot;</InfoPageDarkText>) supplements and forms an integral
              part of our general privacy notice (<InfoPageDarkText>&quot;Privacy Notice&quot;</InfoPageDarkText>). This
              U.S. State Supplement applies to residents of U.S. states that have enacted comprehensive consumer privacy
              legislation, other than California (which is addressed in Appendix A). If not otherwise defined herein,
              capitalized terms have the meaning given to them in our Privacy Notice or Appendix A, as applicable.
              Please read the Privacy Notice and Appendix A for additional terms that may apply to you. This U.S. State
              Supplement applies if you are a resident of any of the following states: Colorado, Connecticut, Delaware,
              Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode
              Island, Tennessee, Texas, Utah, Virginia, or any other U.S. state that has enacted a comprehensive
              consumer privacy law granting residents rights similar to those described below (collectively,{' '}
              <InfoPageDarkText>&quot;Applicable State Privacy Laws&quot;</InfoPageDarkText>). The specific rights
              available to you may vary depending on your state of residence and the requirements of applicable law.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              No Admission of Applicability. We provide the disclosures set out in this U.S. State Supplement, and honor
              the rights described herein, in the interest of transparency and as a matter of good practice.
              Notwithstanding anything to the contrary herein, nothing in this U.S. State Supplement constitutes, and it
              shall not be construed as, an admission, acknowledgment, or representation that any particular Applicable
              State Privacy Law applies to Karma, that Karma meets any applicability or jurisdictional threshold
              (whether based on revenue, volume of personal data processed, derivation of revenue from the sale or
              sharing of personal data, or otherwise) under any Applicable State Privacy Law. We reserve all rights and
              defenses available to us under applicable law, including the right to assert that any Applicable State
              Privacy Law does not apply to us or to a particular individual or processing activity. To the extent an
              Applicable State Privacy Law applies, the rights and disclosures herein are provided only to the extent,
              and subject to the exemptions and limitations, set out in that law.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageDarkText>Your Privacy Rights.</InfoPageDarkText> Depending on your state of residence and subject
              to certain exceptions and limitations under applicable law, you may have the following rights with respect
              to your Personal Data.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageDarkText>Right to Know/Access.</InfoPageDarkText> You may have the right to confirm whether we
              are processing your Personal Data and to access such Personal Data. In some states, you may also have the
              right to obtain a copy of your Personal Data in a portable and, to the extent technically feasible,
              readily usable format.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageDarkText>Right to Correct.</InfoPageDarkText> You may have the right to request that we correct
              inaccuracies in your Personal Data, taking into account the nature of the Personal Data and the purposes
              of the processing.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageDarkText>Right to Delete.</InfoPageDarkText> You may have the right to request that we delete
              Personal Data that you have provided to us or that we have obtained about you, subject to certain
              exceptions permitted by applicable law.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageDarkText>Right to Opt-Out of Sale of Personal Data.</InfoPageDarkText> Certain Applicable State
              Privacy Laws define &quot;sale&quot; of Personal Data broadly to include the exchange of Personal Data for
              monetary or other valuable consideration. To the extent we engage in activities that constitute a
              &quot;sale&quot; under applicable law, you may have the right to opt out of such sale. For more
              information on how to submit an opt-out request, please see &quot;How to Exercise Your Rights&quot; below.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageDarkText>Right to Opt-Out of Targeted Advertising.</InfoPageDarkText> Some Applicable State
              Privacy Laws grant you the right to opt out of the processing of your Personal Data for purposes of
              targeted advertising (sometimes referred to as &quot;cross-context behavioral advertising&quot; or
              &quot;interest-based advertising&quot;). To the extent we process your Personal Data for targeted
              advertising, you may have the right to opt out of such processing. For more information on how to submit
              an opt-out request, please see &quot;How to Exercise Your Rights&quot; below.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageDarkText>Right to Opt-Out of Profiling.</InfoPageDarkText> Some Applicable State Privacy Laws
              grant you the right to opt out of profiling in furtherance of decisions that produce legal or similarly
              significant effects concerning you. We do not currently engage in profiling that produces such effects,
              but if we do in the future, you may have the right to opt out.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageDarkText>Right to Non-Discrimination.</InfoPageDarkText> You have the right not to receive
              discriminatory treatment for exercising any of your privacy rights under Applicable State Privacy Laws.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageDarkText>Opt-Out Preference Signals.</InfoPageDarkText> Some Applicable State Privacy Laws
              require businesses to recognize browser-based or device-based opt-out preference signals (such as the
              Global Privacy Control or similar mechanisms) as valid requests to opt out of the sale of Personal Data,
              sharing for targeted advertising, or certain other processing activities. We honor such opt-out preference
              signals to the extent required by applicable law. If you enable such a signal on your browser or device,
              we will treat it as a valid opt-out request for that browser or device. For more information about the
              Global Privacy Control, please visit{' '}
              <LinkTermsPrivacy href="https://globalprivacycontrol.org/" target="_black">
                https://globalprivacycontrol.org/
              </LinkTermsPrivacy>
              .
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageDarkText>How to Exercise Your Rights.</InfoPageDarkText> To exercise any of the rights described
              above, please submit a verifiable request to us by contacting us at{' '}
              <LinkTermsPrivacy href="mailto:support@karmanow.com" target="_top">
                support@karmanow.com
              </LinkTermsPrivacy>
              . Where available, you may also exercise certain opt-out choices through any cookie consent manager,
              privacy settings, or other privacy choice tools we make available through the Site or App. We will respond
              to your request within the timeframe required by applicable law. To verify your identity, we may require
              you to provide information that matches the information we have on file about you. If you use an
              authorized agent to submit a request on your behalf, we may require written authorization from you and may
              contact you directly to verify your identity.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageDarkText>Appeals.</InfoPageDarkText> If we decline to take action on your request, you may have
              the right to appeal our decision, depending on your state of residence. To submit an appeal, please
              contact us at{' '}
              <LinkTermsPrivacy href="mailto:support@karmanow.com" target="_top">
                support@karmanow.com
              </LinkTermsPrivacy>{' '}
              and include &quot;Privacy Rights Appeal&quot; in the subject line, together with sufficient information
              for us to identify your original request and the basis for your appeal. We will review and respond to your
              appeal within the timeframe required by applicable law. If your appeal is denied, you may have the right
              to contact your state attorney general or other applicable regulatory authority.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageDarkText>Financial Incentives.</InfoPageDarkText> We may offer financial incentives, price or
              service differences, or other programs that relate to the collection, retention, or use of your Personal
              Data. For example, our Rewards Program allows users to earn Coins by making Qualifying Purchases,
              participating in Rewarded Activities, and engaging with our Services, which may be redeemed for gift
              cards, PayPal payments, or other rewards.
              <br />
              <br />
              We offer the Rewards Program because we believe it provides value to our users and enhances their
              experience with our Services. The value of the Personal Data collected in connection with the Rewards
              Program is reasonably related to the overall value of the rewards offered, taking into account the expense
              incurred by us in providing the program, as well as the revenue generated by the program. We calculate
              this value based on the practical and reasonably anticipated costs of operating the Rewards Program,
              including the cost of rewards fulfilled, administrative costs, and the commercial value of the data
              collected.
              <br />
              <br />
              Participation in the Rewards Program is voluntary. You may opt into the Rewards Program by creating an
              account and agreeing to the applicable terms. You may withdraw from the Rewards Program at any time by
              contacting us at{' '}
              <LinkTermsPrivacy href="mailto:support@karmanow.com" target="_top">
                support@karmanow.com
              </LinkTermsPrivacy>{' '}
              or by closing your account. If you withdraw, you will forfeit any unredeemed Coins in accordance with our
              Terms of Service.
            </InfoPageListItem>
            <InfoPageListItem level_2>
              <InfoPageDarkText>Contact Us.</InfoPageDarkText> If you have any questions about this U.S. State
              Supplement or wish to exercise any of your rights as set out herein, please contact us at{' '}
              <LinkTermsPrivacy href="mailto:support@karmanow.com" target="_top">
                support@karmanow.com
              </LinkTermsPrivacy>
              .
            </InfoPageListItem>
          </InfoPageOrderedList>
        </InfoPageListItem>
      </InfoPageOrderedList>
    </>
  );
};

export default PrivacyPageContent;
